/**
 * feedback.js — tiny haptic + audio feedback helpers.
 *
 * Procedurally generates short sounds via the Web Audio API so there are
 * no audio assets to ship. All helpers are safe to call on every device:
 * - On iOS Safari with no user gesture: AudioContext creation is deferred
 *   until the first call (which is itself a user gesture by definition).
 * - On unsupported browsers: silently no-op.
 * - Respects the user's `prefers-reduced-motion` setting for haptics.
 */

let _ctx = null;
const getCtx = () => {
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { _ctx = new AC(); } catch { return null; }
  return _ctx;
};

const prefersReducedMotion = () => {
  try { return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
};

/**
 * Short upward "launch" sweep — used on START MATCH and similar CTAs.
 * ~80ms total, ramps frequency 300 → 900 Hz and tapers volume.
 */
export const playLaunchSound = () => {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(900, now + 0.08);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.1);
  } catch { /* never throw from a feedback helper */ }
};

/**
 * Short haptic buzz. Defaults to 80ms. Vibration API is mostly Android-only;
 * iOS no-ops (which is fine — no haptic is better than a JS error).
 */
export const haptic = (ms = 80) => {
  if (prefersReducedMotion()) return;
  try { navigator.vibrate?.(ms); } catch { /* ignore */ }
};

/**
 * Combined CTA feedback — sound + haptic. Use this on big "launch" buttons.
 */
export const launchFeedback = () => {
  playLaunchSound();
  haptic(80);
};
